// assets/AllCourses/Exercises/CircleTheCorrectWordExercise.jsx

import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { StringField, TextareaField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

export default function CircleTheCorrectWordExercise({
  exercise = {},
  onUpdate,
  errors = {},
  isPreview = false,
showResults = false,  
onComplete = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(exercise.wordChoiceQuestions)
    ? exercise.wordChoiceQuestions
    : [];

  /* ================= STUDENT STATE ================= */
  const [answers, setAnswers] = useState({});
  const completedRef = useRef(false);
React.useEffect(() => {
  if (!showResults) completedRef.current = false;
}, [showResults]);
  /* ================= EDIT HELPERS ================= */
  const updateQuestions = (next) => {
    if (isEditMode) onUpdate("wordChoiceQuestions", next);
  };

  const updateQuestionField = (idx, field, value) => {
    if (!isEditMode) return;

    const next = [...questions];
    next[idx] = {
      sentence: "",
      correct: "",
      requireCapitalization: false,
      ...next[idx],
      [field]: value,
    };

    updateQuestions(next);
  };

  const addQuestion = () => {
    if (!isEditMode) return;
    updateQuestions([
      ...questions,
      {
        sentence: "",
        correct: "",
        requireCapitalization: false,
      },
    ]);
  };

  const removeQuestion = (idx) => {
    if (!isEditMode) return;
    updateQuestions(questions.filter((_, i) => i !== idx));
  };

  /* ================= STUDENT INTERACTION ================= */
  const handleWordClick = (qIdx, word) => {
if (isEditMode || showResults) return;

    const next = { ...answers, [qIdx]: word };
    setAnswers(next);

    const allAnswered = questions.every(
      (_, i) => next[i] !== undefined
    );

    if (allAnswered) checkAnswers(next);
  };

  const checkAnswers = (finalAnswers) => {
    if (completedRef.current) return;
    completedRef.current = true;

    const allCorrect = questions.every((q, idx) => {
      const user = String(finalAnswers[idx] || "").trim();
      const correct = String(q.correct || "").trim();

      return q.requireCapitalization
        ? user === correct
        : user.toLowerCase() === correct.toLowerCase();
    });

    onComplete(exercise._id, allCorrect);
  };

  /* ================= SENTENCE RENDER ================= */
  const renderSentence = (q, qIdx) => {
    if (!q?.sentence) {
      return (
        <span className="text-muted-foreground">
          No sentence provided.
        </span>
      );
    }

    const parts = q.sentence.split(/[\(\)]/);

    return parts.map((part, idx) => {
      if (idx % 2 === 1) {
        const options = part
          .split("/")
          .map(o => o.trim())
          .filter(Boolean);

        return (
          <span key={idx} className="inline-flex flex-wrap gap-2 mx-2">
            {options.map(word => {
              const selected = answers[qIdx] === word;
              const isCorrect =
                word.trim().toLowerCase() ===
                String(q.correct || "")
                  .trim()
                  .toLowerCase();

              return (
                <button
                  key={word}
                  type="button"
                  onClick={() =>
                    handleWordClick(qIdx, word)
                  }
                  disabled={showResults}
                  className={cn(
                    "px-4 py-1.5 rounded-full text-sm border transition-all duration-200",
                    !showResults && "hover:bg-accent",

                    // BEFORE CONTINUE
                    selected && !showResults &&
                      "bg-primary text-primary-foreground border-primary",

                    // AFTER CONTINUE - CORRECT
                    showResults && isCorrect &&
                      "bg-green-100 border-green-300 text-green-800",

                    // AFTER CONTINUE - WRONG SELECTED
                    showResults && selected && !isCorrect &&
                      "bg-red-100 border-red-300 text-red-800",

                    // Disable clicking after result
                    showResults && "cursor-not-allowed opacity-90"
                  )}
                >
                  {word}
                </button>
              );
            })}
          </span>
        );
      }

      return <span key={idx}>{part}</span>;
    });
  };

  /* ================= EDIT MODE ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.map((q, idx) => (
          <Card key={idx} className="p-5">
            <div className="space-y-4">
              <TextareaField
                label={`Sentence ${idx + 1}`}
                value={q.sentence || ""}
                onChange={v =>
                  updateQuestionField(idx, "sentence", v)
                }
                placeholder="The (big/small) house is beautiful."
                rows={3}
                required
                error={
                  errors?.[
                    `exercise-${exercise.index}-wc-sentence-${idx}`
                  ]
                }
              />

              <StringField
                label="Correct Answer"
                value={q.correct || ""}
                onChange={v =>
                  updateQuestionField(idx, "correct", v)
                }
                placeholder="big"
                required
                error={
                  errors?.[
                    `exercise-${exercise.index}-wc-correct-${idx}`
                  ]
                }
              />

              <div className="rounded-md bg-muted/40 p-3 text-xs text-muted-foreground">
                Use <code>( )</code> for choices and{" "}
                <code>/</code> to separate options.
                <br />
                Example:{" "}
                <code>The (big/small) house is beautiful.</code>
              </div>

              <CapitalizationRule
                checked={!!q.requireCapitalization}
                onChange={c =>
                  updateQuestionField(
                    idx,
                    "requireCapitalization",
                    c
                  )
                }
              />

              <Button
                size="sm"
                variant="destructive"
                onClick={() => removeQuestion(idx)}
              >
                Remove Question
              </Button>
            </div>
          </Card>
        ))}

        <Button variant="outline" onClick={addQuestion}>
          Add Question
        </Button>
      </div>
    );
  }

  /* ================= STUDENT MODE ================= */
  if (!questions.length) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>
            No questions available.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="max-w-3xl mx-auto shadow-sm">
      <CardHeader>
        <CardTitle className="text-base">
          Choose the Correct Word
        </CardTitle>
        <CardDescription>
          Click the correct word in each sentence.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {questions.map((q, idx) => (
          <div
            key={idx}
            className="rounded-xl border bg-muted/40 p-5 text-base leading-relaxed"
          >
            {renderSentence(q, idx)}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
